import { useDisclosure, useToast } from "@chakra-ui/react";
import { db, functions } from "connections/firebase";
import {
  updateDoc,
  QueryDocumentSnapshot,
  SnapshotOptions,
  WithFieldValue,
  DocumentData,
  collection,
  doc,
  FirestoreDataConverter,
} from "firebase/firestore";
import _ from "lodash";
import { httpsCallable } from "firebase/functions";
import { FormikHelpers } from "formik";
import { IAdmin, IAdminFormPayload } from "interfaces/admin";
import { useEffect, useState } from "react";
import { useCollectionData } from "react-firebase-hooks/firestore";

const converter: FirestoreDataConverter<IAdmin> = {
  toFirestore(admin: WithFieldValue<IAdmin>): DocumentData {
    return admin;
  },
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options: SnapshotOptions
  ): IAdmin {
    const data = snapshot.data(options);
    return {
      id: snapshot.id,
      name: data.name,
      email: data.email,
      role: data.role,
    };
  },
};

const collection_name = "admins";
const initialValues: IAdminFormPayload = {
  name: "",
  email: "",
  role: "admin",
};

export default function useAdmin() {
  const toast = useToast();
  const [data, loading, error] = useCollectionData(
    collection(db, collection_name).withConverter(converter),
    {
      snapshotListenOptions: { includeMetadataChanges: true },
    }
  );
  const { onClose: onCloseForm, onOpen, isOpen: isOpenForm } = useDisclosure();
  const registerAdminFunction = httpsCallable(functions, "registrationAdmin");
  const deleteAdminFunction = httpsCallable(functions, "deleteAdmin");

  const [formValue, setFormValue] = useState<{
    id: string;
    initialValues: IAdminFormPayload;
  }>({
    id: "",
    initialValues,
  });

  const [loadingAction, setLoadingAction] = useState<null | string>(null);

  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [error, toast]);

  const getAdminDetail = (id: string) => {
    if (data) {
      const selected = data.find((value) => value.id === id);
      if (selected) return selected;
    }

    return null;
  };

  const addAdmin = async (params: IAdminFormPayload) => {
    try {
      setLoadingAction("new");
      await registerAdminFunction(params);
    } catch (error) {
      setLoadingAction(null);
      return Promise.reject(error);
    }

    setLoadingAction(null);
  };

  const updateAdmin = async (id: string, params: IAdminFormPayload) => {
    setLoadingAction(id);
    await updateDoc(doc(db, collection_name, id), params);
    setLoadingAction(null);
  };

  const updateStatusHouse = async (id: string, status: boolean) => {
    setLoadingAction(id);
    await updateDoc(doc(db, collection_name, id), {
      status,
    });
    setLoadingAction(null);
  };

  const deleteAdmin = async (id: string) => {
    try {
      setLoadingAction("new");
      await deleteAdminFunction({ id });
    } catch (error) {
      setLoadingAction(null);
      return Promise.reject(error);
    }

    setLoadingAction(null);
  };

  const openEditForm = (id: string) => {
    onOpen();
    const detail = getAdminDetail(id);
    const initialValues = _.omit(detail, ["id"]);
    setFormValue({
      id,
      initialValues,
    });
  };

  const openAddForm = () => {
    onOpen();
    setFormValue({
      id: "new",
      initialValues,
    });
  };

  const onSubmitForm = async (
    values: IAdminFormPayload,
    formikHelpers: FormikHelpers<IAdminFormPayload>
  ) => {
    if (formValue.id === "new") {
      await addAdmin(values);
    } else {
      await updateAdmin(formValue.id, values);
    }

    onCloseForm();
    formikHelpers.resetForm();
  };

  return {
    data,
    loading,
    addAdmin,
    updateStatusHouse,
    updateAdmin,
    deleteAdmin,
    selectedId: loadingAction,
    isLoadingAction: loadingAction !== null,
    openAddForm,
    openEditForm,
    getAdminDetail,
    formValue,
    isOpenForm,
    onCloseForm,
    onSubmitForm,
  };
}
